/**
 * ENSO UTC presentation: status, count definitions, the Tested timeline note
 * and the acceptance payload. Read-only views of run.enso_utc and the
 * backend's EnsoUTCReview; the backend re-checks every rule on accept.
 */
import type { EiEnsoUtcCounts, EiEnsoUtcGate, EiEnsoUtcReview, EiInvestigationRun } from '../types/engineeringIntelligence';

/** Counts in display order, each with what it counts (backend ensoutc.ReportCounts). */
export const ENSO_UTC_COUNTS: Array<{ key: keyof EiEnsoUtcCounts; label: string; definition: string }> = [
  { key: 'required', label: 'Required', definition: 'Planned cases the canonical categories require for the changed functions.' },
  { key: 'unresolved', label: 'Unresolved', definition: 'Cases whose applicability could not be decided; they stay open, never dropped.' },
  { key: 'planned', label: 'Planned', definition: 'Every case in the plan; each ends in exactly one terminal status.' },
  { key: 'generated', label: 'Generated', definition: 'Planned cases the generator returned a test for, valid or not.' },
  { key: 'valid', label: 'Valid', definition: 'Generated tests that passed structural validation.' },
  { key: 'executed', label: 'Executed', definition: 'Cases with an observed test result (passed + failed).' },
  { key: 'passed', label: 'Passed', definition: 'Cases that passed on the fixed tree.' },
  { key: 'failed', label: 'Failed', definition: 'Cases that ran and failed on the fixed tree.' },
  { key: 'blocked', label: 'Blocked', definition: 'Cases the sandbox could not run (refusal, timeout, build failure).' },
  { key: 'missing', label: 'Missing', definition: 'Planned cases the generator never returned a test for.' },
  { key: 'not_applicable', label: 'N/A', definition: 'Categories judged not applicable, each with a recorded justification.' },
];

export type EnsoUtcStatusKind = 'none' | 'stale' | 'partial' | 'failed' | 'blocked' | 'missing' | 'blocking' | 'unknown' | 'review_required' | 'satisfied';

export const ENSO_UTC_STATUS_LABEL: Record<EnsoUtcStatusKind, string> = {
  none: 'No result',
  stale: 'Stale',
  partial: 'Partial',
  failed: 'Failed',
  blocked: 'Blocked',
  missing: 'Missing tests',
  blocking: 'Gates not met',
  unknown: 'Unknown — review required',
  review_required: 'Review required',
  satisfied: 'Satisfied',
};

export interface EnsoUtcStatus {
  kind: EnsoUtcStatusKind;
  label: string;
  detail: string;
}

const st = (kind: EnsoUtcStatusKind, detail: string): EnsoUtcStatus => ({ kind, label: ENSO_UTC_STATUS_LABEL[kind], detail });

/** The worst condition on the run's ENSO UTC result. Satisfied only when nothing is open. */
export function ensoUtcStatus(run: EiInvestigationRun | null | undefined): EnsoUtcStatus {
  const rec = run?.enso_utc;
  const rep = rec?.report;
  if (!rec || !rep) {
    return st('none', 'No ENSO UTC result for this package — rerun Testing before the results can be accepted or executed.');
  }
  if (rec.stale || rep.stale) return st('stale', `Stale: ${rec.stale_reason || rep.stale_reason || 'the package changed'} — rerun Testing.`);
  if (rec.package_hash !== String(run?.package_hash || '')) return st('stale', 'The ENSO UTC result is for another package — rerun Testing.');
  if (rep.partial) return st('partial', `Partial report: ${rep.partial_reason || 'not every case ran'} — rerun Testing.`);
  // Gate status is the backend's review (testing_acceptance_review.enso_utc), never inferred from counts.
  const review = run?.testing_acceptance_review?.enso_utc ?? null;
  if (review?.problem) return st('stale', review.problem);
  const c = rep.counts;
  if (c.failed > 0) return st('failed', `${c.failed} of ${c.planned} case(s) failed on the fixed tree.`);
  if ((c.test_defect ?? 0) > 0) return st('review_required', `${c.test_defect} case(s) need review: the test failed and nothing points at the patched code.`);
  if (c.blocked > 0) return st('blocked', `${c.blocked} case(s) could not run.`);
  if (c.missing + c.invalid + c.not_executed > 0) {
    return st('missing', `${c.missing} missing, ${c.invalid} invalid, ${c.not_executed} not executed.`);
  }
  if (!review) return st('unknown', 'The backend has not reviewed these gates; nothing here counts as a pass.');
  if (review.blocking.length) return st('blocking', `${review.blocking.length} gate(s) not met by evidence: ${review.blocking.map((g) => g.rule_id).join(', ')}.`);
  if (review.acknowledge.length) return st('review_required', `A reviewer must acknowledge: ${review.acknowledge.map((g) => g.rule_id).join(', ')}.`);
  return st('satisfied', `${c.satisfied} of ${c.required} required case(s) satisfied.`);
}

/**
 * Puts ENSO UTC on the Tested timeline step once Testing produced results.
 * The backend can mark Tested completed at TESTS_READY; a failing, blocked,
 * unresolved or stale result holds it instead.
 */
export function ensoUtcTestedStep<T extends { id?: string; status?: string; detail?: string }>(
  steps: T[],
  run: EiInvestigationRun | null | undefined,
): T[] {
  const state = String(run?.state || '').toUpperCase();
  if (state !== 'TESTS_READY' && !(state === 'NEEDS_HUMAN' && run?.enso_utc)) return steps;
  const s = ensoUtcStatus(run);
  const note = `ENSO UTC: ${s.label} — ${s.detail}`;
  return steps.map((step) =>
    step.id !== 'tested'
      ? step
      : { ...step, status: s.kind === 'satisfied' || s.kind === 'review_required' ? step.status : 'blocked', detail: note },
  );
}

/**
 * The gates an acceptance must acknowledge and the exact backend fields.
 * With risks, the blocking gates are acknowledged as accepted risks too.
 */
export function ensoUtcAcceptance(review: EiEnsoUtcReview, withRisks: boolean, reason: string) {
  const gates: EiEnsoUtcGate[] = [...(withRisks ? review.blocking : []), ...review.acknowledge];
  return {
    gates,
    payload: {
      ensoUtcReportHash: review.report_hash,
      ensoUtcAcknowledgedGates: gates.map((g) => g.rule_id),
      ensoUtcReason: reason.trim(),
    },
  };
}

export const ENSO_UTC_GATE_LABEL: Record<string, string> = {
  unresolved: 'Unresolved',
  not_evaluable: 'Not evaluable',
  review_required: 'Review required',
  satisfied: 'Satisfied',
};

/** One gate as the backend review classifies it. A gate the review does not list is satisfied only if the report says so. */
export function ensoUtcGateState(gate: EiEnsoUtcGate, review: EiEnsoUtcReview | null | undefined) {
  const status = ENSO_UTC_GATE_LABEL[gate.status] ?? gate.status;
  if (review?.blocking.some((g) => g.rule_id === gate.rule_id)) return { kind: 'blocking' as const, label: `Blocking — not met by evidence (${status})` };
  if (review?.acknowledge.some((g) => g.rule_id === gate.rule_id)) return { kind: 'acknowledge' as const, label: `Acknowledgement required (${status})` };
  if (gate.status === 'satisfied') return { kind: 'satisfied' as const, label: 'Satisfied' };
  return { kind: 'unknown' as const, label: `Unknown — review required (${status})` };
}

export const ENSO_UTC_PROOF_LABEL: Record<string, string> = {
  not_required: 'Not required',
  not_attempted: 'Not attempted',
  proven: 'Proven',
  not_proven: 'Not proven',
  inconclusive: 'Inconclusive',
  blocked: 'Blocked',
};

/** The legacy generated regression test, shown only for historical runs without ENSO UTC. */
export function legacyRegression(run: EiInvestigationRun | null | undefined) {
  return run?.enso_utc ? null : (run?.regression_test ?? null);
}
